if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        // Initialize upsell widget on page load
        const variantId = this.querySelector('input[name="id"]')?.value;
        if (variantId) {
          this.updateUpsellWidget(variantId);
        }

        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      renderSelectedVariantImages({ variantId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        const requestUrl = this.buildVariantImageRequestUrl(variantId);

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Variant images fetch aborted by user");
            } else {
              console.error("Error fetching variant images:", error);
            }
          });
      }

      buildVariantImageRequestUrl(variantId) {
        const productUrl = this.dataset.url;
        const params = [`section_id=${this.sectionId}`];

        if (variantId) {
          params.push(`variant=${variantId}`);
        }

        return `${productUrl}?${params.join("&")}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateProductStatus(variant?.id);
          this.updateUpsellWidget(variant?.id);

          // Update Klaviyo BIS trigger visibility
          const klaviyoBIS = this.querySelector(".klaviyo-bis-trigger");
          const buyButtons = document.querySelector(".main-cta-btn");

          if (klaviyoBIS && variant?.id) {
            klaviyoBIS.dataset.product = this.dataset.productId;
            klaviyoBIS.dataset.variant = variant.id;

            if (variant.available) {
              klaviyoBIS.classList.add("hidden");
              buyButtons?.classList.remove("hidden");
            } else {
              klaviyoBIS.classList.remove("hidden");
              buyButtons?.classList.add("hidden");
            }
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          // Update variant images using section rendering API
          if (variant?.id) {
            this.renderSelectedVariantImages({
              variantId: variant.id,
              callback: (imageHtml) => {
                const newVariantGallery =
                  imageHtml.querySelector("variant-gallery");
                const currentVariantGallery =
                  this.querySelector("variant-gallery");

                if (newVariantGallery && currentVariantGallery) {
                  // Use HTMLUpdateUtility for smooth transitions
                  HTMLUpdateUtility.viewTransition(
                    currentVariantGallery,
                    newVariantGallery,
                    this.preProcessHtmlCallbacks,
                    this.postProcessHtmlCallbacks
                  );

                  // Re-initialize variant gallery after update
                  setTimeout(() => {
                    const updatedGallery =
                      this.querySelector("variant-gallery");
                    if (updatedGallery && updatedGallery.initializeSlides) {
                      updatedGallery.initializeSlides();
                      updatedGallery.bindEvents();
                    }
                  }, 100);
                }
              },
            });
          }

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === ""
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination("Low-Stock-Alert", ({ classList }) =>
            classList.contains("hidden")
          );
          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateProductStatus(variantId) {
        const statusElement = this.querySelector(".product__status");
        if (!statusElement || !variantId) return;

        // Get the status data from the script tag
        const statusDataScript = document.getElementById(
          "variant-delivery-data"
        );
        if (!statusDataScript) return;

        const statusData = JSON.parse(statusDataScript.textContent);
        const currentVariantData = statusData[variantId];

        if (!currentVariantData || !currentVariantData.delivery_time) {
          statusElement.classList.add("hidden");
          return;
        }

        if (currentVariantData.available) {
          statusElement.classList.remove("hidden");
        } else {
          statusElement.classList.add("hidden");
        }

        const newStatusText = currentVariantData.delivery_time;
        statusElement.querySelector(".product__status-text").textContent =
          newStatusText;

        // Get colors from the status element's data attributes
        const defaultColor = statusElement.dataset.indicatorDefaultColor;
        const presaleColor = statusElement.dataset.indicatorPresaleColor;

        // Update indicator color based on PRE-SALE text
        const indicatorColor = newStatusText.includes("PRE-SALE")
          ? presaleColor
          : defaultColor;

        statusElement.style.setProperty(
          "--status-indicator-color",
          indicatorColor
        );
        statusElement.dataset.statusText = newStatusText;
      }

      updateUpsellWidget(variantId) {
        const upsellContainer = this.querySelector(".pdp-upsell");
        if (!upsellContainer) return;

        const upsellDataScript = document.getElementById("upsell-variant-data");
        if (!upsellDataScript) return;

        try {
          const upsellData = JSON.parse(upsellDataScript.textContent);
          const currentVariantData = upsellData[variantId];

          if (
            !currentVariantData ||
            !currentVariantData.upsell_variants ||
            currentVariantData.upsell_variants.length === 0
          ) {
            upsellContainer.classList.add("hidden");
            return;
          }

          // Show the container since we have upsell variants
          upsellContainer.classList.remove("hidden");

          // Update the cards container with new upsell variants
          const cardsContainer = upsellContainer.querySelector(
            ".pdp-upsell__cards-container"
          );
          if (cardsContainer) {
            cardsContainer.innerHTML = currentVariantData.upsell_variants
              .map(
                (upsellVariant) => `
              <div class="pdp-upsell__container">
                <div class="pdp-upsell__card">
                  <div class="pdp-upsell__image-container">
                    ${
                      upsellVariant.featured_image
                        ? `<img class="pdp-upsell__image" src="${upsellVariant.featured_image}" alt="${upsellVariant.title}">`
                        : '<div class="pdp-upsell__image placeholder">No image</div>'
                    }
                  </div>
                  <div class="pdp-upsell__content">
                    <h3 class="pdp-upsell__product-title">${
                      upsellVariant.title
                    }</h3>
                    <div class="pdp-upsell__price">${upsellVariant.price}</div>
                    <form
                      method="post"
                      action="/cart/add"
                      id="upsell-product-form-${upsellVariant.id}"
                      accept-charset="UTF-8"
                      class="pdp-upsell__form"
                      enctype="multipart/form-data"
                      data-product-form
                    >
                      <input type="hidden" name="id" value="${
                        upsellVariant.id
                      }">
                      <input type="hidden" name="quantity" value="1">
                      <button
                        type="submit"
                        class="pdp-upsell__button"
                        data-product-id="${upsellVariant.id}"
                        ${!upsellVariant.available ? "disabled" : ""}
                        aria-label="Add ${upsellVariant.title} to cart"
                        data-add-to-cart
                      >
                        <span class="pdp-upsell__button-text">
                          ${upsellVariant.available ? "ADD" : "SOLD OUT"}
                        </span>
                        <span class="pdp-upsell__loading-spinner hidden">
                          <svg
                            aria-hidden="true"
                            focusable="false"
                            class="spinner"
                            viewBox="0 0 66 66"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                          </svg>
                        </span>
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            `
              )
              .join("");

            // Reinitialize the form submit handlers for the new upsell forms
            const upsellForms = cardsContainer.querySelectorAll(
              "[data-product-form]"
            );
            upsellForms.forEach(function (form) {
              const button = form.querySelector("[data-add-to-cart]");
              const buttonText = button.querySelector(
                ".pdp-upsell__button-text"
              );
              const spinner = button.querySelector(
                ".pdp-upsell__loading-spinner"
              );

              form.addEventListener("submit", async function (e) {
                e.preventDefault();

                // Show loading state
                button.disabled = true;
                buttonText.classList.add("hidden");
                spinner.classList.remove("hidden");

                try {
                  const formData = new FormData(form);
                  const response = await fetch("/cart/add", {
                    method: "POST",
                    body: formData,
                  });

                  if (response.ok) {
                    // Call UpCart's register function to update the cart
                    if (typeof window.upcartRegisterAddToCart === "function") {
                      window.upcartRegisterAddToCart();
                    }
                  } else {
                    throw new Error("Failed to add item to cart");
                  }
                } catch (error) {
                  console.error("Error adding item to cart:", error);
                } finally {
                  // Reset button state
                  button.disabled = false;
                  buttonText.classList.remove("hidden");
                  spinner.classList.add("hidden");
                }
              });
            });
          }
        } catch (error) {
          console.error("Error updating upsell widget:", error);
          upsellContainer.classList.add("hidden");
        }
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]")
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId)
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]")
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId)
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`
                  )
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            }
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
